import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { ArrowLeft, Lock, LogOut, Trash2, Check, AlertCircle, Moon, Sun, X } from 'lucide-react';
import { useStore } from '../store';
import { Avatar, Button, GlobalSpin } from '../components/ui';
import { useDarkMode } from '../hooks/useDarkMode';
import { useAuth } from '../hooks';
import { userApi } from '../api/client';
import { toast } from '../components/ui/Toast';

const C = {
  amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623',
  amber400:'#C4811A', amber500:'#8B5A0A',
  ink:'#1A1814', inkLight:'#3D3A34', muted:'#7A7570',
  border:'#E8E4DC', surface:'#FAFAF8', white:'#FFFFFF',
  red:'#DC2626', green:'#16A34A',
};
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

// ─── Confirm modal via portal ─────────────────────────────────────────────────
const ConfirmPortal: React.FC<{
  title: string; message: string; confirmLabel: string; danger?: boolean;
  onConfirm: () => void; onCancel: () => void;
}> = ({ title, message, confirmLabel, danger = true, onConfirm, onCancel }) => {
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  return ReactDOM.createPortal(
    <div onClick={stop} onMouseDown={stop} style={{ position:'fixed', inset:0, zIndex:99999 }}>
      <div onClick={(e) => { stop(e); onCancel(); }}
        style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(4px)' }}/>
      <div onClick={stop} style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:'min(90vw,400px)', background:C.white, borderRadius:20, padding:'28px 28px 22px', boxShadow:'0 24px 64px rgba(0,0,0,0.22)', zIndex:1 }}>
        <button onClick={onCancel} style={{ position:'absolute', top:14, right:14, background:'none', border:'none', cursor:'pointer', color:C.muted, padding:4, display:'flex' }}>
          <X size={16}/>
        </button>
        <h2 style={{ fontFamily:font.head, fontWeight:800, fontSize:19, color:C.ink, marginBottom:8 }}>{title}</h2>
        <p style={{ fontSize:14, color:C.muted, lineHeight:1.75, fontFamily:font.body, marginBottom:24 }}>{message}</p>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={onCancel} style={{ flex:1, height:44, borderRadius:10, border:`1.5px solid ${C.border}`, background:C.surface, cursor:'pointer', fontFamily:font.body, fontWeight:600, fontSize:14, color:'#3D3A34' }}>
            Cancel
          </button>
          <button onClick={onConfirm} style={{ flex:1, height:44, borderRadius:10, border:'none', background: danger ? 'linear-gradient(135deg,#EF4444,#DC2626)' : `linear-gradient(135deg,${C.amber300},${C.amber400})`, cursor:'pointer', fontFamily:font.body, fontWeight:700, fontSize:14, color:C.white }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Section wrapper ──────────────────────────────────────────────────────────
const Section: React.FC<{ title: string; desc?: string; children: React.ReactNode }> = ({ title, desc, children }) => (
  <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:16, padding:'24px 28px', marginBottom:20 }}>
    <div style={{ marginBottom:20, paddingBottom:16, borderBottom:`1px solid ${C.border}` }}>
      <h2 style={{ fontFamily:font.head, fontWeight:700, fontSize:17, color:C.ink, marginBottom:desc?4:0 }}>{title}</h2>
      {desc && <p style={{ fontSize:13, color:C.muted, fontFamily:font.body }}>{desc}</p>}
    </div>
    {children}
  </div>
);

// ─── Settings page ────────────────────────────────────────────────────────────
const Settings: React.FC = () => {
  const { authLoading, isAuthenticated } = useAuth();
  const user       = useStore(s => s.user);
  const setUser    = useStore(s => s.setUser);
  const logout     = useStore(s => s.logout);
  const { isDark, toggle: toggleDark } = useDarkMode();

  const [name,         setName]        = useState('');
  const [nameSaving,   setNameSaving]  = useState(false);
  const [nameSaved,    setNameSaved]   = useState(false);
  const [curPw,        setCurPw]       = useState('');
  const [newPw,        setNewPw]       = useState('');
  const [pwMsg,        setPwMsg]       = useState('');
  const [pwErr,        setPwErr]       = useState(false);
  const [pwLoading,    setPwLoading]   = useState(false);
  const [showDelete,   setShowDelete]  = useState(false);
  const [deleting,     setDeleting]    = useState(false);

  if (authLoading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', background:C.surface, flexDirection:'column', gap:16 }}>
      <div style={{ width:40, height:40, borderRadius:'50%', border:'3px solid #FAEEDA', borderTopColor:C.amber300, animation:'spin .7s linear infinite' }}/>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );

  if (!isAuthenticated) { window.location.replace('/login'); return null; }

  // ── Save name ───────────────────────────────────────────────────────────────
  const saveName = async () => {
    if (!name.trim()) return;
    setNameSaving(true);
    try {
      const updated = await userApi.update({ name: name.trim() });
      if (setUser) setUser(updated);
      else useStore.setState({ user: updated });
      setNameSaved(true);
      toast.success('Name updated!');
      setTimeout(() => setNameSaved(false), 2500);
    } catch (err: any) {
      toast.error(err?.message || 'Could not update name');
    } finally {
      setNameSaving(false);
    }
  };

  // ── Change password ─────────────────────────────────────────────────────────
  const changePassword = async () => {
    setPwErr(false); setPwMsg('');
    if (!curPw || !newPw) { setPwErr(true); setPwMsg('Please fill in both fields.'); return; }
    if (newPw.length < 8)  { setPwErr(true); setPwMsg('New password must be at least 8 characters.'); return; }
    if (curPw === newPw)   { setPwErr(true); setPwMsg('New password must be different from current.'); return; }
    setPwLoading(true);
    try {
      await userApi.changePassword(curPw, newPw);
      setPwErr(false); setPwMsg('Password updated successfully!');
      setCurPw(''); setNewPw('');
      toast.success('Password updated!');
      setTimeout(() => setPwMsg(''), 3000);
    } catch (err: any) {
      setPwErr(true); setPwMsg(err?.message || 'Could not update password');
    } finally {
      setPwLoading(false);
    }
  };

  // ── Delete account ──────────────────────────────────────────────────────────
  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await userApi.deleteAccount();
      logout();
    } catch (err: any) {
      toast.error(err?.message || 'Could not delete account');
      setDeleting(false);
      setShowDelete(false);
    }
  };

  return (
    <>
      <GlobalSpin/>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        body{font-family:'DM Sans',sans-serif;background:${C.surface};color:${C.ink};}
        .sett-input{width:100%;height:42px;padding:0 14px;border-radius:9px;border:1.5px solid ${C.border};font-size:14px;font-family:'DM Sans',sans-serif;color:${C.ink};outline:none;transition:border-color .15s;background:${C.white};}
        .sett-input:focus{border-color:${C.amber300};box-shadow:0 0 0 3px ${C.amber300}22;}
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>

      <div style={{ minHeight:'100vh', background:C.surface }}>
        {/* Top bar */}
        <div style={{ background:C.white, borderBottom:`1px solid ${C.border}`, padding:'0 24px', height:60, display:'flex', alignItems:'center', gap:14 }}>
          <a href="/dashboard" style={{ display:'flex', alignItems:'center', gap:6, color:C.muted, textDecoration:'none', fontSize:13, fontFamily:font.body, fontWeight:500 }}>
            <ArrowLeft size={15}/> Back to notes
          </a>
          <div style={{ flex:1 }}/>
          {user && (
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <Avatar name={user.name} size={30}/>
              <span style={{ fontSize:14, fontWeight:600, color:C.ink, fontFamily:font.body }}>{user.name}</span>
            </div>
          )}
        </div>

        <div style={{ maxWidth:640, margin:'0 auto', padding:'36px 24px 60px' }}>
          <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:28, letterSpacing:'-0.04em', color:C.ink, marginBottom:28 }}>
            Settings
          </h1>

          {/* ── Profile ─────────────────────────────────────────────────────── */}
          <Section title="Profile" desc="Update your display name.">
            {user && (
              <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:20 }}>
                <Avatar name={user.name} size={52}/>
                <div>
                  <p style={{ fontSize:15, fontWeight:700, color:C.ink, fontFamily:font.body, marginBottom:2 }}>{user.name}</p>
                  <p style={{ fontSize:13, color:C.muted, fontFamily:font.body, marginBottom:6 }}>{user.email}</p>
                  <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20, background:C.amber100, color:C.amber500, fontFamily:font.body }}>
                    {user.plan === 'pro' ? '⭐ Pro plan' : 'Free plan'}
                  </span>
                </div>
              </div>
            )}
            <div style={{ display:'flex', gap:10, alignItems:'flex-end' }}>
              <div style={{ flex:1 }}>
                <label style={{ fontSize:12, fontWeight:600, color:C.inkLight, fontFamily:font.body, display:'block', marginBottom:5 }}>
                  Display name
                </label>
                <input
                  className="sett-input" type="text"
                  value={name} onChange={e => setName(e.target.value)}
                  placeholder={user?.name || 'Your name'}
                  onKeyDown={e => e.key === 'Enter' && saveName()}
                />
              </div>
              <button
                onClick={saveName} disabled={nameSaving || !name.trim()}
                style={{ height:42, padding:'0 18px', borderRadius:9, border:'none', cursor: nameSaving ? 'wait' : 'pointer', background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:13, fontFamily:font.body, display:'flex', alignItems:'center', gap:6, flexShrink:0, opacity: !name.trim() ? 0.6 : 1 }}
              >
                {nameSaved ? <><Check size={13}/>Saved!</> : nameSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </Section>

          {/* ── Password ─────────────────────────────────────────────────────── */}
          <Section title="Password" desc="Change your account password.">
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:C.inkLight, fontFamily:font.body, display:'block', marginBottom:5 }}>Current password</label>
                <input className="sett-input" type="password" value={curPw} onChange={e => setCurPw(e.target.value)} placeholder="••••••••"/>
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:C.inkLight, fontFamily:font.body, display:'block', marginBottom:5 }}>New password</label>
                <input className="sett-input" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="At least 8 characters"
                  onKeyDown={e => e.key === 'Enter' && changePassword()}/>
              </div>
              {pwMsg && (
                <div style={{ display:'flex', alignItems:'center', gap:6, padding:'10px 12px', borderRadius:8, background:pwErr?'#FEF2F2':'#F0FDF4', border:`1px solid ${pwErr?C.red+'44':C.green+'44'}` }}>
                  {pwErr ? <AlertCircle size={13} color={C.red}/> : <Check size={13} color={C.green}/>}
                  <span style={{ fontSize:13, color:pwErr?C.red:C.green, fontFamily:font.body }}>{pwMsg}</span>
                </div>
              )}
              <Button onClick={changePassword} loading={pwLoading} size="sm" style={{ alignSelf:'flex-start' }}>
                <Lock size={13}/> Update password
              </Button>
            </div>
          </Section>

          {/* ── Appearance ───────────────────────────────────────────────────── */}
          <Section title="Appearance" desc="Choose how Jötr looks on this device.">
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 16px', borderRadius:10, border:`1px solid ${C.border}`, background:C.surface }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:38, height:38, borderRadius:11, background:isDark?'#22201C':C.amber100, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {isDark ? <Moon size={17} color={C.amber300}/> : <Sun size={17} color={C.amber400}/>}
                </div>
                <div>
                  <p style={{ fontSize:14, fontWeight:600, color:C.ink, fontFamily:font.body }}>{isDark ? 'Dark mode' : 'Light mode'}</p>
                  <p style={{ fontSize:12, color:C.muted, fontFamily:font.body }}>Saved to this device automatically.</p>
                </div>
              </div>
              <div onClick={toggleDark} style={{ width:48, height:26, borderRadius:13, background:isDark?C.amber300:C.border, cursor:'pointer', position:'relative', transition:'background .2s', flexShrink:0 }}>
                <div style={{ width:20, height:20, borderRadius:'50%', background:C.white, position:'absolute', top:3, left:isDark?25:3, transition:'left .2s', boxShadow:'0 1px 4px rgba(0,0,0,0.2)' }}/>
              </div>
            </div>
          </Section>

          {/* ── Account ──────────────────────────────────────────────────────── */}
          <Section title="Account" desc="Manage your Jötr account.">
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 16px', borderRadius:10, border:`1px solid ${C.border}`, background:C.surface }}>
                <div>
                  <p style={{ fontSize:14, fontWeight:600, color:C.ink, fontFamily:font.body }}>Sign out</p>
                  <p style={{ fontSize:12, color:C.muted, fontFamily:font.body }}>Sign out of Jötr on this device.</p>
                </div>
                <Button variant="ghost" size="sm" onClick={logout}>
                  <LogOut size={13}/> Sign out
                </Button>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 16px', borderRadius:10, border:`1px solid ${C.red}33`, background:'#FEF2F2' }}>
                <div>
                  <p style={{ fontSize:14, fontWeight:600, color:C.red, fontFamily:font.body }}>Delete account</p>
                  <p style={{ fontSize:12, color:C.muted, fontFamily:font.body }}>Permanently deletes your account and all notes.</p>
                </div>
                <Button variant="danger" size="sm" onClick={() => setShowDelete(true)}>
                  <Trash2 size={13}/> Delete
                </Button>
              </div>
            </div>
          </Section>

          <p style={{ textAlign:'center', fontSize:12, color:C.muted, fontFamily:font.body, marginTop:8 }}>
            Jötr v1.0.0 · <a href="/privacy" style={{ color:C.amber400 }}>Privacy</a> · <a href="/terms" style={{ color:C.amber400 }}>Terms</a>
          </p>
        </div>
      </div>

      {/* Delete account confirmation */}
      {showDelete && (
        <ConfirmPortal
          title="Delete your account?"
          message="This will permanently delete your account, all your notes, and all your folders. This cannot be undone."
          confirmLabel={deleting ? 'Deleting…' : 'Yes, delete everything'}
          onConfirm={deleteAccount}
          onCancel={() => setShowDelete(false)}
        />
      )}
    </>
  );
};

export default Settings;